import { useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Flame, Clock, MapPin, CheckCircle2,
  Loader2, PhoneCall, ShieldCheck, KeyRound, Languages, Sparkles, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import SeoHead from "../components/SeoHead";
import { LOCALITIES } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { whatsappLink } from "../data/content";
import { api, formatApiErrorDetail } from "../lib/api";
import { getFirebaseAuth, firebaseEnabled, RecaptchaVerifier, signInWithPhoneNumber } from "../lib/firebase";

const STEP_LABELS = ["Puja", "Details", "Location", "Contact"];
// Puja-specific slots — a scheduled puja needs a real date/time, not "right now".
const PUJA_SLOTS = [
  { id: "morning", label: "Morning (6 AM - 11 AM)" },
  { id: "midday", label: "Midday (11 AM - 2 PM)" },
  { id: "afternoon", label: "Afternoon (2 PM - 5 PM)" },
  { id: "evening", label: "Evening (5 PM - 8 PM)" },
];
// Minimum selectable date in IST (so it is never "yesterday" for Indian users before 05:30).
const istMinDate = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().split("T")[0];

export default function HirePanditPage() {
  const navigate = useNavigate();
  const { pandit, loading } = useServices();
  const pujas = pandit?.services || [];
  const languages = pandit?.languages || ["Hindi", "Marathi", "Sanskrit", "English"];

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [idToken, setIdToken] = useState("");
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);

  const [form, setForm] = useState({
    puja: null,
    language: "",
    samagri: true,
    date: "",
    slot: "",
    instructions: "",
    locality: "",
    address: "",
    pincode: "",
    landmark: "",
    name: "",
    phone: "",
    email: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toE164 = () => {
    const d = form.phone.replace(/\D/g, "");
    return d.length > 10 ? `+${d}` : `+91${d}`;
  };

  const resetRecaptcha = () => {
    if (recaptchaRef.current) { try { recaptchaRef.current.clear(); } catch { /* noop */ } }
    recaptchaRef.current = null;
    confirmationRef.current = null;
  };

  const firebaseOtpError = (err) => {
    const code = err?.code || "";
    if (code === "auth/unauthorized-domain") return "This website isn't authorized for OTP yet. Please contact support.";
    if (code === "auth/billing-not-enabled") return "SMS OTP is temporarily unavailable. Please try again later.";
    if (code === "auth/invalid-phone-number") return "Please enter a valid mobile number.";
    if (code === "auth/too-many-requests") return "Too many attempts. Please wait a few minutes and try again.";
    if (code === "auth/invalid-verification-code" || code === "auth/code-expired") return "Incorrect or expired OTP. Please request a new one.";
    return (err?.message || "").replace("Firebase: ", "").replace(/\s*\(auth\/.*\)\.?/, "") || "Could not send OTP. Please try again.";
  };

  const sendOtp = async () => {
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone)) { toast.error("Enter a valid mobile number first"); return; }
    setSendingOtp(true);
    try {
      if (firebaseEnabled) {
        const auth = getFirebaseAuth();
        if (!recaptchaRef.current) {
          recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
        }
        confirmationRef.current = await signInWithPhoneNumber(auth, toE164(), recaptchaRef.current);
        setOtpSent(true);
        toast.success("OTP sent to your mobile via SMS");
      } else {
        const { data } = await api.post("/otp/send", { phone: form.phone });
        setOtpSent(true);
        toast.success(`Demo OTP sent: ${data.demo_otp}`, { duration: 8000 });
      }
    } catch (err) {
      resetRecaptcha();
      toast.error(firebaseOtpError(err));
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    try {
      if (firebaseEnabled) {
        const cred = await confirmationRef.current.confirm(otpCode);
        const token = await cred.user.getIdToken();
        setIdToken(token);
        setOtpVerified(true);
        toast.success("Mobile number verified!");
      } else {
        await api.post("/otp/verify", { phone: form.phone, code: otpCode });
        setOtpVerified(true);
        toast.success("Mobile number verified!");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || firebaseOtpError(err));
    }
  };

  const canNext = useMemo(() => {
    if (step === 0) return !!form.puja;
    if (step === 1) return !!form.language && !!form.date && !!form.slot;
    if (step === 2) return form.locality && form.address.trim().length > 3 && /^\d{6}$/.test(form.pincode);
    if (step === 3) return form.name.trim() && /^[0-9+\-\s]{10,15}$/.test(form.phone);
    return false;
  }, [step, form]);

  const estimate = useMemo(() => {
    if (!form.puja) return 0;
    return (form.puja.price || 0) + (form.samagri ? (form.puja.samagri_price || 0) : 0);
  }, [form.puja, form.samagri]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: form.puja.id,
        service_name: form.puja.name,
        category_id: pandit.id,
        problem_description: form.instructions.trim() || `Booking for ${form.puja.name}`,
        locality: form.locality,
        address: form.address,
        landmark: form.landmark,
        pincode: form.pincode,
        preferred_date: form.date,
        preferred_slot: PUJA_SLOTS.find((s) => s.id === form.slot)?.label || form.slot,
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email,
        phone_verified: otpVerified,
        firebase_id_token: idToken,
        puja: {
          puja_name: form.puja.name,
          language: form.language,
          samagri_required: form.samagri,
          instructions: form.instructions.trim(),
          estimated_amount: estimate,
        },
      });
      setConfirmation(data.booking);
      toast.success("Puja booking confirmed!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <SeoHead title="Puja Booking Confirmed - ApnaFix" />
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center af-fade-up" data-testid="pandit-booking-confirmation">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-amber-50 text-amber-600 mx-auto"><CheckCircle2 className="w-9 h-9" /></span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Puja Booking Confirmed!</h1>
          <p className="mt-2 text-slate-600">We're arranging a verified Pandit Ji for your <span className="font-semibold text-navy">{confirmation.service_name}</span> in {confirmation.locality}.</p>
          <div className="mt-6 text-left bg-slate-50 rounded-2xl p-5 space-y-2 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono-num font-semibold text-slate-900">{confirmation.ref}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Puja</span><span className="font-medium text-slate-900">{confirmation.service_name}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">When</span><span className="font-medium text-slate-900">{confirmation.preferred_slot || "To be scheduled"}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-medium text-amber-600">{String(confirmation.status || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || "Received"}</span></p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="pandit-track-btn" onClick={() => navigate(`/track/${confirmation.ref}`)} className="h-11 px-5 bg-brand-orange hover:bg-orange-600 text-white font-semibold">Track my booking</Button>
            <a href={whatsappLink(`Hi ApnaFix, I just booked ${confirmation.service_name} (ID ${confirmation.ref}).`)} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5">
              <PhoneCall className="w-4 h-4" /> Chat on WhatsApp
            </a>
            <Button variant="outline" onClick={() => navigate("/")} className="h-11 px-5 font-semibold">Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SeoHead title="Hire a Pandit Ji - Book Puja Online | ApnaFix" description="Book a verified Pandit Ji for Griha Pravesh, Satyanarayan, Ganesh Puja, Havan and more. Choose language, samagri and slot online." />
      {/* Hero */}
      <div className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14 grid lg:grid-cols-[1.3fr_1fr] gap-8 items-center">
          <div className="af-fade-up">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> Back</Link>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 border border-amber-300/20 px-3 py-1.5 text-xs font-semibold text-amber-200">
              <Flame className="w-3.5 h-3.5" /> Verified Pandit Ji near you
            </span>
            <h1 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">Hire a Pandit Ji<br /><span className="text-brand-orange">For Every Occasion.</span></h1>
            <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-lg">{pandit?.description || "Griha Pravesh, Satyanarayan, Havan & more — book an experienced Pandit Ji with samagri in your preferred language."}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {languages.slice(0, 8).map((l) => (
                <span key={l} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs"><Languages className="w-3.5 h-3.5 text-amber-300" /> {l}</span>
              ))}
            </div>
            <Button data-testid="pandit-become-cta" variant="ghost" onClick={() => navigate("/pandit-register")} className="mt-5 text-amber-200 hover:text-white hover:bg-white/10 h-9 text-sm">
              <UserPlus className="w-4 h-4 mr-1" /> Are you a Pandit Ji? Register here
            </Button>
          </div>
          {pandit?.image_url && (
            <div className="relative af-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                <img src={pandit.image_url} alt="Pandit Ji performing a puja" className="w-full h-[260px] sm:h-[340px] object-cover" loading="eager" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2">
          {STEP_LABELS.map((lbl, i) => (
            <div key={lbl} className="flex-1 flex items-center gap-1 sm:gap-2">
              <div className={`flex items-center gap-2 ${i <= step ? "text-navy" : "text-slate-400"}`}>
                <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-400"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
                <span className="hidden sm:inline text-xs font-semibold">{lbl}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-0.5 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 min-h-[340px]">
          {/* STEP 0: Select Puja */}
          {step === 0 && (
            <div className="af-fade-up">
              <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Which puja do you need?</h2>
              {loading && pujas.length === 0 ? (
                <div className="grid sm:grid-cols-2 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse" />)}</div>
              ) : pujas.length === 0 ? (
                <div data-testid="pandit-empty-state" className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
                  <p className="font-semibold text-slate-900">Pujas are temporarily unavailable</p>
                  <p className="mt-1 text-sm text-slate-500">We couldn't load the puja list right now. Please refresh, or reach us on WhatsApp and we'll arrange a Pandit Ji for you.</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                    <Button data-testid="pandit-empty-retry" variant="outline" onClick={() => window.location.reload()} className="font-semibold">Refresh</Button>
                    <a href={whatsappLink("Hi ApnaFix, I'd like to book a Pandit Ji for a puja.")} target="_blank" rel="noreferrer"
                       className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 px-4"><PhoneCall className="w-4 h-4" /> WhatsApp Us</a>
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {pujas.map((p) => {
                    const active = form.puja?.id === p.id;
                    return (
                      <button key={p.id} data-testid={`pandit-puja-${p.id}`} onClick={() => { set("puja", p); setStep(1); }}
                              className={`text-left rounded-xl border p-4 transition-all ${active ? "border-brand-orange ring-2 ring-orange-200 bg-orange-50" : "border-slate-200 hover:border-orange-300"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="grid place-items-center w-10 h-10 rounded-lg bg-amber-50 text-amber-600"><Flame className="w-5 h-5" /></span>
                          <span className="text-right">
                            <span className="block font-mono-num font-bold text-navy">₹{p.price}</span>
                            {p.duration && <span className="block text-[11px] text-slate-400">{p.duration}</span>}
                          </span>
                        </div>
                        <p className="mt-2 font-semibold text-slate-900 leading-snug">{p.name}</p>
                        {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                      </button>
                    );
                  })}
                </div>
              )}
              {pandit?.cancel_policy && !loading && pujas.length > 0 && (
                <p data-testid="pandit-cancel-policy" className="mt-4 text-xs text-slate-400 leading-relaxed">{pandit.cancel_policy}</p>
              )}
            </div>
          )}

          {/* STEP 1: Details */}
          {step === 1 && form.puja && (
            <div className="af-fade-up space-y-5">
              <h2 className="font-display font-bold text-lg text-slate-900">Puja details</h2>
              <div>
                <Label className="text-sm">Preferred language</Label>
                <Select value={form.language} onValueChange={(v) => set("language", v)}>
                  <SelectTrigger data-testid="pandit-language-select" className="mt-1.5 h-11"><SelectValue placeholder="Select language" /></SelectTrigger>
                  <SelectContent>{languages.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-amber-50 text-amber-600"><Sparkles className="w-4 h-4" /></span>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Include samagri (puja items)</p>
                    <p className="text-xs text-slate-500">Pandit Ji brings all required items {form.puja.samagri_price ? `(+₹${form.puja.samagri_price})` : ""}</p>
                  </div>
                </div>
                <Switch data-testid="pandit-samagri-switch" checked={form.samagri} onCheckedChange={(v) => set("samagri", v)} />
              </div>
              <div>
                <Label className="text-sm">Preferred date</Label>
                <Input data-testid="pandit-date-input" type="date" value={form.date} min={istMinDate()} onChange={(e) => set("date", e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-sm">Preferred time</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-3">
                  {PUJA_SLOTS.map((s) => (
                    <button key={s.id} data-testid={`pandit-slot-${s.id}`} onClick={() => set("slot", s.id)}
                            className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all ${form.slot === s.id ? "border-brand-orange bg-orange-50 text-orange-900 ring-2 ring-orange-200" : "border-slate-200 text-slate-700 hover:border-orange-300"}`}>
                      <Clock className="w-4 h-4 text-brand-orange" /> {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm">Special instructions (optional)</Label>
                <Textarea data-testid="pandit-instructions-input" value={form.instructions} onChange={(e) => set("instructions", e.target.value)} className="mt-1.5" placeholder="e.g. Need puja completed before 11 AM, specific rituals, etc." />
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-between text-sm">
                <span className="text-slate-600">Estimated total</span>
                <span className="font-mono-num font-bold text-navy text-base" data-testid="pandit-estimate">₹{estimate}</span>
              </div>
            </div>
          )}

          {/* STEP 2: Location */}
          {step === 2 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">Where is the puja?</h2>
              <div>
                <Label className="text-sm">Locality</Label>
                <Select value={form.locality} onValueChange={(v) => set("locality", v)}>
                  <SelectTrigger data-testid="pandit-locality-select" className="mt-1.5 h-11"><SelectValue placeholder="Select your area" /></SelectTrigger>
                  <SelectContent>{LOCALITIES.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Full address (flat / wing / building)</Label>
                <Textarea data-testid="pandit-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="e.g. A-1203, Sunrise Towers, Mahavir Nagar" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm">Pincode</Label>
                <Input data-testid="pandit-pincode-input" value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit pincode" inputMode="numeric" maxLength={6} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-sm">Landmark (optional)</Label>
                <Input data-testid="pandit-landmark-input" value={form.landmark} onChange={(e) => set("landmark", e.target.value)} placeholder="Near City Mall" className="mt-1.5 h-11" />
              </div>
            </div>
          )}

          {/* STEP 3: Contact */}
          {step === 3 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">Your contact details</h2>
              <div>
                <Label className="text-sm">Full name</Label>
                <Input data-testid="pandit-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-sm">Mobile number</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="pandit-phone-input" value={form.phone} disabled={otpVerified}
                         onChange={(e) => { set("phone", e.target.value); setOtpSent(false); setOtpVerified(false); setOtpCode(""); setIdToken(""); resetRecaptcha(); }}
                         placeholder="10-digit mobile" className="h-11" />
                  {!otpVerified && (
                    <Button type="button" data-testid="pandit-send-otp-btn" variant="outline" onClick={sendOtp} disabled={sendingOtp} className="h-11 whitespace-nowrap font-semibold">
                      {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? "Resend" : "Send OTP"}
                    </Button>
                  )}
                </div>
                {otpVerified ? (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600"><ShieldCheck className="w-4 h-4" /> Mobile verified</p>
                ) : otpSent ? (
                  <div className="mt-2 flex gap-2">
                    <div className="relative flex-1">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input data-testid="pandit-otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Enter 6-digit OTP" className="h-11 pl-9 font-mono-num" maxLength={6} />
                    </div>
                    <Button type="button" data-testid="pandit-verify-otp-btn" onClick={verifyOtp} disabled={otpCode.length < 4} className="h-11 bg-navy hover:bg-navy-900 text-white font-semibold">Verify</Button>
                  </div>
                ) : null}
              </div>
              <div id="recaptcha-container" />
              <div>
                <Label className="text-sm">Email (optional)</Label>
                <Input data-testid="pandit-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="mt-1.5 h-11" />
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm flex items-start gap-3">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-500 text-white shrink-0"><Flame className="w-4 h-4" /></span>
                <div>
                  <p className="font-semibold text-slate-900">{form.puja?.name} • <MapPin className="w-3 h-3 inline" /> {form.locality}</p>
                  <p className="text-slate-500 text-xs mt-0.5">{form.language} • {PUJA_SLOTS.find((s) => s.id === form.slot)?.label} • Est. ₹{estimate}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {step < 3 ? (
            <Button data-testid="pandit-next-btn" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="bg-brand-orange hover:bg-orange-600 text-white font-semibold px-6">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid="pandit-confirm-btn" disabled={!canNext || submitting || !otpVerified} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Confirming...</> : !otpVerified ? "Verify mobile to confirm" : <>Confirm Puja Booking <Check className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
